import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  ILessonPlan,
  ILessonPlanResponse,
} from '../../../../apis/academics/academicsLessonPlans.interfaces';
import { useAcademicsLessonPlansHook } from '../hooks/useAcademicsLessonPlansHook';
import { TPaginationParams } from '../../../../apis/__shared/shared.interfaces';
import { useAcademicsFiltersContext } from './academicsFilters.context';

type TLessonPlanMode = 'add' | 'edit' | null;

type TAcademicsLessonPlansState = {
  errorMessage?: string;
  isLoading: boolean;
  paginationParams: TPaginationParams;
  totalCount: number;
  allLessonPlans: ILessonPlan[];
  filteredLessonPlans: ILessonPlan[];
  selectedLessonPlanId: string | null;
  selectedLessonPlan: ILessonPlan | null;
  mode: TLessonPlanMode;
};

enum ACTION_TYPE {
  SET_LESSON_PLANS = 'SET_LESSON_PLANS',
  SET_FILTERED_LESSON_PLANS = 'SET_FILTERED_LESSON_PLANS',
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
  SET_PAGINATION_PARAMS = 'SET_PAGINATION_PARAMS',
  SET_SELECTED_LESSON_PLAN_ID = 'SET_SELECTED_LESSON_PLAN_ID',
  SET_SELECTED_LESSON_PLAN = 'SET_SELECTED_LESSON_PLAN',
  SET_MODE = 'SET_MODE',
}

type Action =
  | { type: ACTION_TYPE.SET_LESSON_PLANS; payload: ILessonPlanResponse }
  | { type: ACTION_TYPE.SET_FILTERED_LESSON_PLANS; payload: ILessonPlan[] }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string }
  | { type: ACTION_TYPE.SET_PAGINATION_PARAMS; payload: TPaginationParams }
  | { type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN_ID; payload: string | null }
  | {
      type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN;
      payload: ILessonPlan | null;
    }
  | { type: ACTION_TYPE.SET_MODE; payload: TLessonPlanMode };

const initialState: TAcademicsLessonPlansState = {
  allLessonPlans: [],
  filteredLessonPlans: [],
  selectedLessonPlanId: null,
  selectedLessonPlan: null,
  errorMessage: undefined,
  isLoading: false,
  paginationParams: {
    page: 1,
    limit: 10,
  },
  totalCount: 0,
  mode: null,
};

const AcademicsLessonPlansContext = createContext<
  TAcademicsLessonPlansState | undefined
>(undefined);
const AcademicsLessonPlansDispatchContext = createContext<
  Dispatch<Action> | undefined
>(undefined);

const academicsLessonPlansReducer = (
  state: TAcademicsLessonPlansState,
  action: Action
): TAcademicsLessonPlansState => {
  switch (action.type) {
    case ACTION_TYPE.SET_LESSON_PLANS:
      return {
        ...state,
        allLessonPlans: action.payload.lessonPlans,
        filteredLessonPlans: action.payload.lessonPlans,
        totalCount: action.payload.total,
      };
    case ACTION_TYPE.SET_FILTERED_LESSON_PLANS:
      return {
        ...state,
        filteredLessonPlans: action.payload,
      };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...state, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...state, isLoading: false, errorMessage: action.payload };
    case ACTION_TYPE.SET_PAGINATION_PARAMS:
      return { ...state, paginationParams: action.payload };
    case ACTION_TYPE.SET_SELECTED_LESSON_PLAN_ID:
      return { ...state, selectedLessonPlanId: action.payload };
    case ACTION_TYPE.SET_SELECTED_LESSON_PLAN:
      return { ...state, selectedLessonPlan: action.payload };
    case ACTION_TYPE.SET_MODE:
      return { ...state, mode: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const AcademicsLessonPlansProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(
    academicsLessonPlansReducer,
    initialState
  );
  const { academicYear, grade } = useAcademicsFiltersContext();
  const lessonPlansParams = {
    ...state.paginationParams,
    ...(academicYear && { academicYear }),
    ...(grade && { grade }),
  };
  const { data: lessonPlansResponse, isLoading } = useAcademicsLessonPlansHook(
    lessonPlansParams
  );

  const filteredLessonPlans = useMemo(() => {
    if (!state.allLessonPlans || state.allLessonPlans.length === 0) return [];
    return state.allLessonPlans;
  }, [state.allLessonPlans]);

  useEffect(() => {
    dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: isLoading });
  }, [isLoading]);

  useEffect(() => {
    if (lessonPlansResponse && lessonPlansResponse.lessonPlans) {
      dispatch({
        type: ACTION_TYPE.SET_LESSON_PLANS,
        payload: lessonPlansResponse as ILessonPlanResponse,
      });
    }
  }, [lessonPlansResponse]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_FILTERED_LESSON_PLANS,
      payload: filteredLessonPlans,
    });
  }, [filteredLessonPlans]);

  useEffect(() => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN_ID,
      payload: null,
    });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN, payload: null });
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: null });
  }, [state.paginationParams]);

  const value = useMemo(
    () => ({
      ...state,
      isLoading,
    }),
    [state, isLoading]
  );

  return (
    <AcademicsLessonPlansContext.Provider value={value}>
      <AcademicsLessonPlansDispatchContext.Provider value={dispatch}>
        {children}
      </AcademicsLessonPlansDispatchContext.Provider>
    </AcademicsLessonPlansContext.Provider>
  );
};

export const useAcademicsLessonPlansState = () => {
  const context = useContext(AcademicsLessonPlansContext);
  if (context === undefined) {
    throw new Error(
      'useAcademicsLessonPlansState must be used within an AcademicsLessonPlansProvider'
    );
  }
  return context;
};

export const useAcademicsLessonPlansActions = () => {
  const dispatch = useContext(AcademicsLessonPlansDispatchContext);
  if (dispatch === undefined) {
    throw new Error(
      'useAcademicsLessonPlansActions must be used within an AcademicsLessonPlansProvider'
    );
  }
  const updatePaginationParams = (params: TPaginationParams) => {
    dispatch({ type: ACTION_TYPE.SET_PAGINATION_PARAMS, payload: params });
  };

  const setSelectedLessonPlanId = (id: string | null) => {
    dispatch({ type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN_ID, payload: id });
  };

  const setSelectedLessonPlan = (lessonPlan: ILessonPlan | null) => {
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN,
      payload: lessonPlan,
    });
  };

  const setMode = (mode: TLessonPlanMode) => {
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: mode });
  };

  const openAddMode = () => {
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: 'add' });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN_ID, payload: null });
  };

  const openEditMode = (lessonPlan: ILessonPlan) => {
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: 'edit' });
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN,
      payload: lessonPlan,
    });
    dispatch({
      type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN_ID,
      payload: lessonPlan.id,
    });
  };

  const closeMode = () => {
    dispatch({ type: ACTION_TYPE.SET_MODE, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN, payload: null });
    dispatch({ type: ACTION_TYPE.SET_SELECTED_LESSON_PLAN_ID, payload: null });
  };

  return {
    updatePaginationParams,
    setSelectedLessonPlanId,
    setSelectedLessonPlan,
    setMode,
    openAddMode,
    openEditMode,
    closeMode,
  };
};
